import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useEpic } from '../../../api/tasks-client/tasks-client.hooks';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import {
  setInitiativePerformance,
  setPerformanceActivePanel,
  setPerformanceTab,
} from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { InitiativePerformanceFilters } from '../initiative-performance/initiative-performance-filters';
import { InitiativePerformanceMainSection } from '../initiative-performance/initiative-performance-main-section';
import { InitiativePerformanceSideSection } from '../initiative-performance/initiative-performance-side-section';
import { useSideSection } from '../initiative-performance/initiative-performance.hooks';
import { PerformanceTab } from '../initiative-performance/initiative-performance.type';
import { StrategyPerformanceTabs } from '../strategy-performance/strategy-performance-tabs';
import { transformEpicToInitiativeFormat } from '../strategy-performance/strategy-performance.hooks';
import { StrategyEntityType } from '../strategy.type';
import { EpicDetailsControlBar } from './epic-details-control-bar';
import { EpicDetailsHeader } from './epic-details-header';
import { EpicDetailsOverview } from './epic-details-overview';

export const EpicDetails = () => {
  useDocumentTitle('Epic Details - Bloomfilter');

  const i18n = useTranslation();
  const { id } = useParams();
  const { epic, query } = useEpic(id);
  const navigate = useNavigate();
  const performanceActivePanel = useStrategyStore((state) => state.performanceActivePanel);
  const showSideSection = useSideSection();

  // Set default tab to throughput on mount
  useEffect(() => {
    setPerformanceTab(PerformanceTab.Throughput);
    setPerformanceActivePanel(Panel.Filters);
  }, []);

  // Handle epic not found error
  useEffect(() => {
    if (query.error) {
      navigate('/application/strategy/epics');
    }
  }, [query.error, navigate]);

  // Transform and store epic data for charts to use
  useEffect(() => {
    if (epic) {
      const transformed = transformEpicToInitiativeFormat(epic);
      setInitiativePerformance(transformed);
    }
    return () => {
      setInitiativePerformance(null);
    };
  }, [epic]);

  const showSideFilters = performanceActivePanel === Panel.Filters;

  const setShowSideFilters = (show: boolean) => {
    setPerformanceActivePanel(show ? Panel.Filters : null);
  };

  return (
    <MenuDrivenContainer header={<EpicDetailsHeader />}>
      <BrandedLoadingOverlay visible={query.isLoading} variant="colored" transitionDuration={30} />
      <PageLayout
        controlBar={<EpicDetailsControlBar />}
        overview={{
          title: i18n.t('strategy.overview.title'),
          content: <EpicDetailsOverview />,
        }}
        sideFilters={{
          title: i18n.t('common.filters'),
          content: <InitiativePerformanceFilters setShow={setShowSideFilters} />,
          show: showSideFilters,
          setShow: setShowSideFilters,
        }}
        tabs={<StrategyPerformanceTabs entityType={StrategyEntityType.Epic} />}
        mainSection={<InitiativePerformanceMainSection />}
        sideSection={<InitiativePerformanceSideSection />}
        showSideSection={showSideSection}
        data-page-layout
      />
    </MenuDrivenContainer>
  );
};
